import {
  Building2,
  ExternalLink,
  MailPlus,
  Search,
  User,
  UserRoundPlus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import PlatformLink from '@/components/common/PlatformLink';
import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import { useModal } from '@/components/providers/ModalProvider/ModalProvider';
import { Button } from '@/components/shadcn/button';
import useSelectedParty from '@/features/analysis/hooks/useSelectedParty';
import useCrm from '@/features/crm/hooks/useCrm';
import OnesuranceBadges from '@/features/onesurance/components/OnesuranceBadges/OnesuranceBadges';
import useHasOnesuranceConnection from '@/features/onesurance/hooks/useHasOnesuranceConnection';
import { getPartyName } from '@/lib/party';

import PartyDetailsSection from '../../PartyDetailsSection/PartyDetailsSection';
import PartySearchModal from '../PartySearchModal';

export default function SelectedParty() {
  const { t } = useTranslation();
  const { showModal } = useModal();

  const party = useSelectedParty();
  const { linkParty } = useEmail();

  const {
    canExportEmail,
    exportEmail,
    canExportPartyToMissingCrms,
    exportPartyToMissingCrms,
  } = useCrm();

  const hasOnesuranceConnection = useHasOnesuranceConnection();

  const hasCrmActions = canExportEmail || canExportPartyToMissingCrms;

  const handleShowPartySearch = () =>
    showModal(
      PartySearchModal,
      {
        onSubmit: linkParty,
      },
      {
        title: t('SIDEBAR_PARTY_SEARCH'),
        size: 'full',
      },
    );

  if (!party) return null;

  const name = getPartyName(party);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex w-full items-center justify-between">
        <div className="text-foreground flex items-center gap-1.5 text-sm">
          {party.type === 'COMPANY' ? (
            <Building2 className="size-3.5" />
          ) : (
            <User className="size-3.5" />
          )}

          {name}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="icon-sm"
            onClick={handleShowPartySearch}
          >
            <Search />
          </Button>

          <Button variant="secondary" size="icon-sm" asChild>
            <PlatformLink
              to={`/parties/${party.id}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ExternalLink />
            </PlatformLink>
          </Button>
        </div>
      </div>

      <PartyDetailsSection />

      {hasOnesuranceConnection && <OnesuranceBadges />}

      {hasCrmActions && (
        <div className="flex gap-1">
          {canExportEmail && (
            <Button size="xs" variant="secondary" onClick={exportEmail}>
              <MailPlus />

              {t('EXPORT_EMAIL_BUTTON')}
            </Button>
          )}

          {canExportPartyToMissingCrms && (
            <Button
              size="xs"
              variant="secondary"
              onClick={exportPartyToMissingCrms}
            >
              <UserRoundPlus />

              {t('EXPORT_PARTY_BUTTON')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
